import localforage from 'localforage'
import { productsAPI } from 'api/products'
import { checkConnectivity } from 'utils/connectivityCheck'

/** Shown when offline and cached product lacks units / track_expiry. */
export const PRODUCT_DETAIL_OFFLINE_INCOMPLETE_AR =
 'بيانات المنتج غير مكتملة في وضع عدم الاتصال. يرجى المزامنة عند الاتصال.'

export type ProductUnitDetail = {
 id: number
 name: string
 is_base?: boolean
 factor_to_base?: number | string
 barcode?: string | null
}

/** Full product shape required for forms (purchase, inventory, etc.). */
export type ProductDetail = {
 id: number
 name?: string
 barcode?: string | null
 barcodes?: string[] | null
 unit?: string | null
 cost_price?: number
 selling_price?: number
 track_expiry: boolean
 track_inventory?: boolean
 category_id?: number | null
 category?: { id: number; name?: string } | null
 units?: ProductUnitDetail[]
 updated_at?: string | null
 [key: string]: unknown
}

export type EnsureProductDetailResult = {
 product: ProductDetail | null
 complete: boolean
 /** True when offline and only partial data is available. */
 offlineIncomplete: boolean
 source: 'memory' | 'offline-store' | 'api' | 'partial' | 'none'
}

const memoryCache = new Map<number, ProductDetail>()
const inflight = new Map<number, Promise<EnsureProductDetailResult>>()

const detailStore = localforage.createInstance({
 name: 'erp-offline',
 storeName: 'product_details',
})

function detailStorageKey(productId: number): string {
 return `product:${productId}`
}

export function hasLegacyUnitFallback(product: unknown): boolean {
 if (!product || typeof product !== 'object') return false
 const unit = (product as ProductDetail).unit
 return typeof unit === 'string' && unit.trim() !== ''
}

/**
 * True when the object has enough fields for unit/expiry/inventory form behavior.
 * Partial search results must not pass this check.
 */
export function isProductDetailComplete(product: unknown): product is ProductDetail {
 if (product == null || typeof product !== 'object' || !('id' in product)) return false
 const p = product as ProductDetail
 if (typeof p.track_expiry !== 'boolean') return false
 if (p.track_inventory === undefined || p.track_inventory === null) return false
 if (Array.isArray(p.units)) return true
 return hasLegacyUnitFallback(p)
}

export function parseProductFromApi(raw: unknown): ProductDetail | null {
 const p = (raw as { data?: unknown })?.data ?? raw
 if (!p || typeof p !== 'object' || !('id' in p)) return null
 const product = p as Record<string, unknown>
 const id = Number(product.id)
 if (!id || Number.isNaN(id)) return null
 return normalizeProductDetail({
 ...product,
 id,
 units: Array.isArray(product.units) ? (product.units as ProductUnitDetail[]) : undefined,
 track_expiry: product.track_expiry === true,
 track_inventory:
 product.track_inventory === false || product.track_inventory === true
 ? product.track_inventory
 : product.track_inventory === 0
 ? false
 : product.track_inventory === 1
 ? true
 : product.track_inventory !== undefined
 ? Boolean(product.track_inventory)
 : true,
 })
}

export function normalizeProductDetail(partial: Partial<ProductDetail> & { id: number }): ProductDetail {
 return {
 ...partial,
 id: Number(partial.id),
 track_expiry: partial.track_expiry === true,
 track_inventory: partial.track_inventory === false ? false : true,
 units: Array.isArray(partial.units) ? partial.units : undefined,
 }
}

/**
 * Detail wins over summary. Summary may refresh label/price only without removing units/track_expiry.
 */
export function mergeProductCache(
 existing: ProductDetail | undefined,
 incoming: Partial<ProductDetail> & { id?: number },
): ProductDetail {
 const id = Number(incoming.id ?? existing?.id)
 if (!id || Number.isNaN(id)) {
 return normalizeProductDetail({ ...(existing ?? {}), ...(incoming as ProductDetail), id: 0 })
 }

 if (!existing) {
 return normalizeProductDetail({ ...(incoming as ProductDetail), id })
 }

 const incomingComplete = isProductDetailComplete(incoming)
 const existingComplete = isProductDetailComplete(existing)

 if (existingComplete && !incomingComplete) {
 return normalizeProductDetail({
 ...existing,
 id,
 name: incoming.name ?? existing.name,
 barcode: incoming.barcode ?? existing.barcode,
 barcodes: incoming.barcodes ?? existing.barcodes,
 cost_price: incoming.cost_price ?? existing.cost_price,
 selling_price: incoming.selling_price ?? existing.selling_price,
 stock_quantity: (incoming as { stock_quantity?: number }).stock_quantity ?? (existing as { stock_quantity?: number }).stock_quantity,
 })
 }

 if (incomingComplete && !existingComplete) {
 return normalizeProductDetail(Object.assign({}, existing, incoming, { id }) as ProductDetail)
 }

 return normalizeProductDetail(Object.assign({}, existing, incoming, { id }) as ProductDetail)
}

function setMemoryCache(product: ProductDetail): ProductDetail {
 const merged = mergeProductCache(memoryCache.get(product.id), product)
 memoryCache.set(product.id, merged)
 return merged
}

async function persistOfflineDetail(product: ProductDetail): Promise<void> {
 if (!isProductDetailComplete(product)) return
 try {
 await detailStore.setItem(detailStorageKey(product.id), {
 product,
 cachedAt: Date.now(),
 })
 } catch {
 /* ignore persistence errors */
 }
}

async function readOfflineDetail(productId: number): Promise<ProductDetail | null> {
 try {
 const row = await detailStore.getItem<{ product: ProductDetail; cachedAt: number }>(
 detailStorageKey(productId),
 )
 return row?.product ?? null
 } catch {
 return null
 }
}

export async function invalidateProductDetail(productId: number): Promise<void> {
 memoryCache.delete(productId)
 try {
 await detailStore.removeItem(detailStorageKey(productId))
 } catch {
 /* ignore */
 }
 if (typeof window !== 'undefined') {
 window.dispatchEvent(
 new CustomEvent('product-detail:invalidated', { detail: { productId } }),
 )
 }
}

export async function invalidateAllProductDetails(): Promise<void> {
 memoryCache.clear()
 try {
 await detailStore.clear()
 } catch {
 /* ignore */
 }
 if (typeof window !== 'undefined') {
 window.dispatchEvent(new CustomEvent('product-detail:invalidated', { detail: {} }))
 }
}

/** Store lightweight search rows without downgrading existing detail cache. */
export function cacheSummaryProduct(summary: Partial<ProductDetail> & { id: number }): ProductDetail {
 return setMemoryCache(mergeProductCache(memoryCache.get(summary.id), summary))
}

/** Seed detail cache from list/index payloads (e.g. fetchProducts, offline bootstrap). */
export function seedProductDetailsFromList(products: unknown[]): void {
 if (!Array.isArray(products)) return
 for (const raw of products) {
 const parsed = parseProductFromApi(raw)
 if (parsed && isProductDetailComplete(parsed)) {
 const merged = setMemoryCache(parsed)
 void persistOfflineDetail(merged)
 }
 }
}

export type EnsureProductDetailOptions = {
 forceRefresh?: boolean
}

export async function ensureProductDetail(
 productId: number | string,
 options: EnsureProductDetailOptions = {},
): Promise<EnsureProductDetailResult> {
 const id = Number(productId)
 if (!id || Number.isNaN(id)) {
 return { product: null, complete: false, offlineIncomplete: false, source: 'none' }
 }

 const existingInflight = inflight.get(id)
 if (existingInflight && !options.forceRefresh) {
 return existingInflight
 }

 const run = async (): Promise<EnsureProductDetailResult> => {
 const cached = memoryCache.get(id)
 if (cached && isProductDetailComplete(cached) && !options.forceRefresh) {
 return { product: cached, complete: true, offlineIncomplete: false, source: 'memory' }
 }

 const online = await checkConnectivity()

 if (online) {
 try {
 const raw = await productsAPI.getById(id)
 const parsed = parseProductFromApi(raw)
 if (parsed) {
 const merged = setMemoryCache(parsed)
 await persistOfflineDetail(merged)
 const complete = isProductDetailComplete(merged)
 return {
 product: merged,
 complete,
 offlineIncomplete: false,
 source: complete ? 'api' : 'partial',
 }
 }
 } catch {
 /* fall through to offline store / partial memory */
 }
 }

 const offlineRow = await readOfflineDetail(id)
 if (offlineRow) {
 const merged = setMemoryCache(offlineRow)
 const complete = isProductDetailComplete(merged)
 return {
 product: merged,
 complete,
 offlineIncomplete: !complete && !online,
 source: 'offline-store',
 }
 }

 const partial = cached ?? null
 const complete = partial != null && isProductDetailComplete(partial)
 return {
 product: partial,
 complete,
 offlineIncomplete: !complete && !online,
 source: partial ? 'partial' : 'none',
 }
 }

 const promise = run().finally(() => {
 inflight.delete(id)
 })
 inflight.set(id, promise)
 return promise
}

export function defaultUnitIdForProduct(product: ProductDetail): string {
 const units = Array.isArray(product.units) ? product.units : []
 const base = units.find((u) => u.is_base) ?? units[0]
 return base?.id != null ? String(base.id) : ''
}

export function getCachedProductDetail(productId: number): ProductDetail | undefined {
 return memoryCache.get(productId)
}
